/**
 * content/index.js - the game's declaration, and the check that its content
 * agrees with the engine.
 *
 * A game plugs into the GAME-LEVEL-ENGINE by declaring what it covers and then
 * never contradicting the engine about anything else. validateContent() is that
 * promise made checkable: it is run by client/test/bubblePopContent.test.js and
 * is the file a future game should copy first, because every error it can
 * produce is a mistake somebody is going to make.
 */

import {
  getLevel,
  getPromptStyle,
  getStrictness,
  getBoxesForLevel,
  SUPPORT_BY_ID,
  CAPABILITY_IDS,
  validateCapabilities,
  validateCoverage,
} from '../../../../engine/framework.js';

import { LEVELS, COVERAGE, pacingFor, getLevelContent } from './levels.js';
import { OBJECTS, CARRIERS, getObject } from './objects.js';
import { PRAISE, ENCOURAGEMENT, NEARLY, ASSIST, PROMPTS, HINTS, LEVEL_INTRO } from './voice.js';

/**
 * The game, as the engine sees it.
 *
 * `capabilities` is a MIRROR, not the source of truth. The server reads
 * games.engine_capabilities from the registry, deliberately, so a client can
 * never assert what it instruments (ENGINE-BUILD.md section 7.3). This copy
 * exists so the game knows which trial fields it is obliged to fill, and
 * database/register_bubble_pop.sql must agree with it. A test asserts that.
 */
export const GAME = Object.freeze({
  id: 'bubble-pop',
  name: 'Bubble Pop',
  description:
    'Bubbles drift up carrying things. Say what you see and they pop. '
    + 'Phases 1 to 3: echoic single words, word retrieval with fading support, '
    + 'and two-word requests.',
  phasesAvailable: COVERAGE.phasesAvailable,
  // The level dimension of section 8.7. Passed straight to the engine, which
  // honours it in placement and in every navigation call.
  levelsAvailable: Object.freeze(COVERAGE.levelsAvailable),
  capabilities: Object.freeze({ prompt_sequence: true, audio_energy: true }),
  minAge: 2,
  maxAge: 8,
  skillTags: Object.freeze(['speech', 'vocabulary', 'requesting', 'early-words']),
  totalLevels: LEVELS.length,
});

/** Praise/encouragement pools, in the shape logic/lineBag.js consumes. */
export const POOLS = Object.freeze({
  praise: PRAISE,
  encouragement: ENCOURAGEMENT,
  nearly: NEARLY,
  assist: ASSIST,
});

/**
 * Check this game's content against the engine and against its own rules.
 *
 * @returns {{ok:boolean, errors:string[], warnings:string[], summary:object}}
 */
export function validateContent() {
  const errors = [];
  const warnings = [];

  // ---- the capability declaration is one the engine recognises -----------
  const caps = validateCapabilities(GAME.capabilities);
  if (!caps.ok) errors.push(...caps.errors);

  // ---- and so is the coverage declaration --------------------------------
  const coverage = validateCoverage({
    phasesAvailable: GAME.phasesAvailable,
    levelsAvailable: GAME.levelsAvailable,
  });
  if (!coverage.ok) errors.push(...coverage.errors);
  if (coverage.ok && coverage.availableLevels.length !== LEVELS.length) {
    errors.push(
      `Coverage declares ${coverage.availableLevels.length} levels but the content has `
      + `${LEVELS.length}. A level the engine believes in and the game cannot load is `
      + 'exactly what levelsAvailable exists to prevent.'
    );
  }

  // ---- every level exists in the framework, at the right phase -----------
  const seen = new Set();
  for (const content of LEVELS) {
    const where = `${content.id} (phase ${content.phase} level ${content.level})`;

    if (!GAME.phasesAvailable.includes(content.phase)) {
      errors.push(`${where}: phase is not in phasesAvailable [${GAME.phasesAvailable}].`);
    }

    const engineLevel = getLevel(content.phase, content.level);
    if (!engineLevel) {
      errors.push(`${where}: no such level in the framework.`);
      continue;
    }

    const key = `${content.phase}.${content.level}`;
    if (seen.has(key)) errors.push(`${where}: two content entries claim the same framework level.`);
    seen.add(key);

    // ---- the opening rung is inside the phase's own bounds ---------------
    // Lower ladder index = MORE support. maxSupport is the most supportive
    // rung the phase permits; minSupport the least.
    const rung = SUPPORT_BY_ID[content.nominalRung];
    if (!rung) {
      errors.push(`${where}: nominalRung "${content.nominalRung}" is not a SUPPORT_LADDER rung.`);
    } else {
      const { maxSupport, minSupport } = getPromptStyle(content.phase).support;
      if (rung.index < maxSupport || rung.index > minSupport) {
        errors.push(
          `${where}: nominalRung "${content.nominalRung}" (index ${rung.index}) is outside `
          + `phase ${content.phase}'s bounds ${maxSupport}..${minSupport}. `
          + 'A game may choose where in the phase a level sits; it may not leave the phase.'
        );
      }
    }

    // ---- there is a prompt line for every rung the level can reach -------
    const phasePrompts = PROMPTS[content.phase] || {};
    if (!phasePrompts[content.nominalRung] || !phasePrompts[content.nominalRung].length) {
      errors.push(`${where}: no prompt lines for the opening rung "${content.nominalRung}".`);
    }

    // ---- the pool is real, and big enough not to repeat ------------------
    if (!content.pool.length) errors.push(`${where}: empty object pool.`);
    for (const id of content.pool) {
      if (!getObject(id)) errors.push(`${where}: unknown object "${id}".`);
    }
    if (content.pool.length < content.targetsPerRound + 1) {
      errors.push(
        `${where}: pool of ${content.pool.length} cannot fill ${content.targetsPerRound} `
        + 'target(s) and still vary between rounds.'
      );
    }
    if (content.bubblesOnScreen < content.targetsPerRound) {
      errors.push(`${where}: bubblesOnScreen (${content.bubblesOnScreen}) is fewer than targetsPerRound.`);
    }
    if (content.bubblesOnScreen > content.pool.length) {
      errors.push(
        `${where}: bubblesOnScreen (${content.bubblesOnScreen}) exceeds the pool `
        + `(${content.pool.length}); the field cannot be filled without a duplicate.`
      );
    }

    // ---- no two objects in one pool are minimal pairs --------------------
    // A mis-recognition that lands on another object IN THE SAME FIELD would
    // be scored as a correct production of a different target. Discriminating
    // similar names is Phase 3 Level 4, which this game does not implement.
    for (let i = 0; i < content.pool.length; i += 1) {
      for (let j = i + 1; j < content.pool.length; j += 1) {
        const a = getObject(content.pool[i]);
        const b = getObject(content.pool[j]);
        if (minimalPair(a.word, b.word)) {
          errors.push(`${where}: "${a.word}" and "${b.word}" are a minimal pair in the same pool.`);
        }
        const overlap = a.accept.filter((x) => b.accept.includes(x));
        if (overlap.length) {
          errors.push(
            `${where}: "${a.word}" and "${b.word}" both accept ${overlap.join(', ')}. `
            + 'One transcript would score two different targets.'
          );
        }
      }
    }

    // ---- Phase 3 carries carriers; nothing else does ---------------------
    if (content.phase === 3) {
      if (!content.carriers || !content.carriers.length) {
        errors.push(`${where}: phase 3 is two-word manding and no carriers are offered.`);
      }
      for (const c of content.carriers || []) {
        if (!CARRIERS[c]) errors.push(`${where}: unknown carrier "${c}".`);
      }
    } else if (content.carriers) {
      errors.push(`${where}: carriers are a phase 3 idea; phase ${content.phase} must not offer them.`);
    }

    // ---- pacing is ordered and fits inside the response window -----------
    const pacing = pacingFor(content);
    const escalations = pacing.escalateAtMs || [];
    for (let i = 1; i < escalations.length; i += 1) {
      if (escalations[i] <= escalations[i - 1]) {
        errors.push(`${where}: escalateAtMs is not strictly increasing.`);
      }
    }
    if (escalations.length && escalations[escalations.length - 1] >= pacing.windowMs) {
      errors.push(
        `${where}: the last escalation (${escalations[escalations.length - 1]}ms) is at or after `
        + `the response window closes (${pacing.windowMs}ms), so it would never be seen.`
      );
    }
    if (pacing.hintAtMs >= pacing.windowMs) {
      errors.push(`${where}: hintAtMs is at or after the window closes.`);
    }

    // ---- the level intro speaks to the phase -----------------------------
    if (!LEVEL_INTRO[content.phase] || !LEVEL_INTRO[content.phase].length) {
      errors.push(`${where}: no level intro lines for phase ${content.phase}.`);
    }
    if (!HINTS[content.phase] || !HINTS[content.phase].length) {
      errors.push(`${where}: no hint lines for phase ${content.phase}.`);
    }
  }

  // ---- section 8.5: 100+ unique praise phrases ---------------------------
  const praiseText = PRAISE.map((p) => p.text);
  if (praiseText.length < 100) {
    errors.push(`Only ${praiseText.length} praise phrases; section 8.5 requires 100+.`);
  }
  if (new Set(praiseText).size !== praiseText.length) {
    errors.push('Duplicate praise phrases: the pool must be 100+ UNIQUE phrases.');
  }

  // ---- phase 1's prompt must contain the word, and later phases must not --
  // Section 8.2: a Phase 1 stage that does not have Pip speak the word aloud
  // is not a Phase 1 stage. The mirror of that rule matters just as much: a
  // Phase 2 prompt that names the object has not faded anything.
  for (const line of PROMPTS[1].full_model) {
    if (!line.includes('{word}')) {
      errors.push(`Phase 1 prompt "${line}" does not speak the target word (section 8.2).`);
    }
  }
  for (const rung of ['visual', 'gestural']) {
    for (const [phase, byRung] of Object.entries(PROMPTS)) {
      for (const line of byRung[rung] || []) {
        if (line.includes('{word}') || line.includes('{phrase}')) {
          errors.push(
            `Phase ${phase} "${rung}" prompt "${line}" contains the answer. `
            + 'That rung exists precisely to withhold it.'
          );
        }
      }
    }
  }

  // ---- prompts evolve: commands early, questions later ------------------
  const openEnded = (lines) => lines.filter((l) => l.trim().endsWith('?')).length;
  if (openEnded(PROMPTS[2].visual) === 0) {
    warnings.push('No open-ended question among the phase 2 visual prompts.');
  }
  if (openEnded(PROMPTS[3].gestural) === 0) {
    warnings.push('No open-ended question among the phase 3 gestural prompts.');
  }

  // ---- clues never contain their own answer ------------------------------
  for (const object of Object.values(OBJECTS)) {
    if (object.clue.toLowerCase().includes(object.word.toLowerCase())) {
      errors.push(`The clue for "${object.word}" contains the word itself; that is a model, not a clue.`);
    }
  }

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    summary: {
      levels: LEVELS.length,
      phases: [...new Set(LEVELS.map((l) => l.phase))],
      objects: Object.keys(OBJECTS).length,
      praise: praiseText.length,
      encouragement: ENCOURAGEMENT.length,
      nearly: NEARLY.length,
      assist: ASSIST.length,
      capabilities: CAPABILITY_IDS.filter((id) => GAME.capabilities[id]),
      boxesPerLevel: LEVELS.map((l) => ({
        level: `${l.phase}.${l.level}`,
        boxes: getBoxesForLevel(l.phase, l.level).map((b) => b.code),
        strictness: getStrictness(l.phase).id,
      })),
    },
  };
}

/**
 * Two words differing by exactly one phoneme-ish character in the same
 * position, or by one at the start. Crude on purpose: it exists to catch
 * cat/hat/bat in one pool at authoring time, not to do phonology.
 */
function minimalPair(a, b) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) diff += 1;
  return diff === 1;
}

export { LEVELS, COVERAGE, getLevelContent, pacingFor } from './levels.js';
export { OBJECTS, CARRIERS, getObject, getCarrier, acceptedPhrases, canonicalPhrase } from './objects.js';
export * as VOICE from './voice.js';

export default { GAME, POOLS, validateContent };
